import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";
import { Pagination, SortItem } from "./commonApiTypes";

const CUSTOMER_PORTAL_SESSION_CREATE = gql`
  mutation CustomerPortalSessionCreate($params: CustomerPortalSessionInput!) {
    customerPortalSessionCreate(params: $params) {
      code
      data {
        url
        type
        customerId
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const SUBSCRIPTION_UPDATE = gql`
  mutation SubscriptionUpdate($params: SubscriptionUpdateInput!) {
    subscriptionUpdate(params: $params) {
      code
      data {
        appPlanCode
        appPlanPriceId
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const SUBSCRIPTION_CANCEL = gql`
  mutation SubscriptionCancel($params: SubscriptionCancelInput!) {
    subscriptionCancel(params: $params) {
      code
      data {
        periodEnd
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const SUBSCRIPTION_HISTORY_LIST = gql`
  query SubscriptionHistoryList(
    $filter: SubscriptionHistoryFilter
    $params: PaginationAndSorting
  ) {
    subscriptionHistoryList(filter: $filter, params: $params) {
      code
      data {
        id
        amount
        currency
        date
        status
        description
        invoiceUrl
        hostedUrl
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

export interface CustomerPortalSession {
  url?: string | null | undefined;
  type?: string | null | undefined;
  customerId?: string | null | undefined;
}

export interface CustomerPortalSessionInput {
  appPlanCode?: string | null | undefined;
  appPlanPriceId?: string | null | undefined;
  returnUrl?: string | null | undefined;
}

export interface SubscriptionUpdate {
  appPlanCode?: string | null | undefined;
}

export interface SubscriptionUpdateInput {
  appPlanCode: string;
  appPlanPriceId: string;
}

export interface SubscriptionCancel {
  periodEnd?: string | null | undefined;
}

export interface SubscriptionCancelInput {
  reason?: string | null | undefined;
}

export interface SubscriptionHistoryItem {
  id?: string | null | undefined;
  amount?: number | null | undefined;
  currency?: string | null | undefined;
  date?: string | null | undefined;
  status?: string | null | undefined;
  description?: string | null | undefined;
  invoiceUrl?: string | null | undefined;
  hostedUrl?: string | null | undefined;
}

export interface SubscriptionHistoryFilter {
  id?: string | null | undefined;
}

/**
 * Customer Portal service that handles customer portal sessions and subscription management
 */
export class CustomerPortalService {
  /**
   * Create a new customer portal session
   */
  static async createSession(
    params: CustomerPortalSessionInput
  ): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      CUSTOMER_PORTAL_SESSION_CREATE,
      {
        params,
      }
    );
    return result;
  }

  /**
   * Update subscription plan
   */
  static async updateSubscription(
    params: SubscriptionUpdateInput
  ): Promise<OpResult> {
    const result = await ApiService.executeMutation(SUBSCRIPTION_UPDATE, {
      params,
    });
    return result;
  }

  /**
   * Cancel subscription
   */
  static async cancelSubscription(
    params: SubscriptionCancelInput
  ): Promise<OpResult> {
    const result = await ApiService.executeMutation(SUBSCRIPTION_CANCEL, {
      params,
    });
    return result;
  }

  /**
   * Get subscription history list with optional filtering, pagination, and sorting
   */
  static async getSubscriptionHistory(
    filter?: SubscriptionHistoryFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    const result = await ApiService.executeQuery(SUBSCRIPTION_HISTORY_LIST, {
      filter: filter || {},
      params: {
        pagination: pagination || undefined,
        sortBy: sortBy || undefined,
      },
    });
    return result;
  }
}
